import React from "react";
import {Box, Text, Stack, PseudoBox, PseudoBoxProps} from "@chakra-ui/core";
import Image from "@pency/components/display/Image";

import {useTranslation} from "~/i18n/hooks";
import {Product} from "~/product/types";
import {usePrice} from "~/i18n/hooks";
import TruncatedText from "~/ui/feedback/TruncatedText";
import {getVariantsPriceRange} from "~/product/selectors";
import { formatImageUrl } from "~/product/utils/formatImage";
import ProductBadges from "~/product/components/ProductBadges";

interface Props extends Omit<PseudoBoxProps, "onClick"> {
  product: Product;
  onClick?: (product: Product) => void;
  isRaised?: boolean;
}

const LandscapeProductCard: React.FC<Props> = ({isRaised = false, product, onClick, ...props}) => {
  const p = usePrice();
  const t = useTranslation();
  const {images, title, price, originalPrice, description, type, currentStock, handleStock} = product;
  const [min, max] = getVariantsPriceRange(product.options);
  let typeProduct = type;

  function handleClick() {
    onClick && onClick(product);
  }

  // If we get here by any point, return null
  if (type === "hidden") return null;
  if (handleStock && Number.isInteger(currentStock) && currentStock <= 0) typeProduct = "unavailable";
  return (
    <PseudoBox
      backgroundColor="page"
      borderColor="borderStrong"
      borderWidth={1}
      boxShadow={isRaised ? "lg" : "none"}
      cursor={onClick ? "pointer" : "inherit"}
      padding={4}
      rounded="md"
      transition="all 0.2s"
      {...props}
    >
      <Stack
        isInline
        alignItems={{base: "center", sm: "stretch"}}
        data-test-id="product"
        height="100%"
        justifyContent="space-between"
        position="relative"
        spacing={4}
        onClick={handleClick}
      >
        <Box
          display="flex"
          flex={1}
          flexDirection="column"
          height="100%"
          justifyContent="space-between"
          minHeight={{base: 24, sm: "9rem"}}
          width="100%"
        >
          <Stack marginBottom={2} spacing={1}>
            <ProductBadges badges={product.badges} />
            <TruncatedText fontWeight={500} lineHeight="normal" lines={2}>
              {title}
            </TruncatedText>
            {description && (
              <TruncatedText color="subtle" display="block" fontSize="sm" lines={2}>
                {description}
              </TruncatedText>
            )}
          </Stack>
          <Stack spacing={2}>
            {(type === "available" || type === "unavailable") && (
              <Stack isInline alignItems="center">
                <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
                  {p(price)}
                </Text>
              </Stack>
            )}
            {type === "promotional" && (
              <Stack isInline alignItems="center">
                <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
                  {p(price)}
                </Text>
                {originalPrice && (
                  <Text color="subtle" fontSize="sm" lineHeight={1} textDecoration="line-through">
                    {p(originalPrice)}
                  </Text>
                )}
              </Stack>
            )}
            {type === "variant" && (
              <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
                {min === max ? p(min) : `${p(min)} ~ ${p(max)}`}
              </Text>
            )}
            {type === "ask" && typeProduct !== "unavailable" && (
              <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
                {t("home.productStatus.ask")}
              </Text>
            )}
            {typeProduct === "unavailable" && (
              <Text color="yellow.500" fontSize="sm" fontWeight={500} lineHeight={1}>
                {t("home.productStatus.outStock")}
              </Text>
            )}
            {handleStock && Number.isInteger(currentStock) && currentStock > 0 && currentStock <= 5 && (
              <Text color="orange.500" fontSize="sm" fontWeight={500} lineHeight={1}>
                {`Últimas ${currentStock} disponibles`}
              </Text>
            )}
          </Stack>
        </Box>
        {images?.length && (
          <Image
            fadeIn
            height={{base: 24, sm: "9rem"}}
            minWidth={{base: 24, sm: "9rem"}}
            rounded="md"
            src={formatImageUrl(images?.[0])}
            width={{base: 24, sm: "9rem"}}
          />
        )}
      </Stack>
    </PseudoBox>
  );
};

export default LandscapeProductCard;
